import { Client } from "./Client";

export enum AUDIENCE_LIST_STATUSES {
  NOTIFICATION_RECEIVED = 0,
  PROCESSING = 1,
  PROCESSED = 2,
  FAILED = 3,
  INITIALIZED = 4,
  REINDEXING = 5,
}

export const IMPORT_METHODS = {
  CSV: 'csv',
  SALES_NAVIGATOR: 'salesNavigator'
} as const;

export const TAB_KEYS = {
  ALL: 'all',
  PROCESSING: 'processing',
  READY: 'ready',
  FAILED: 'failed',
  ARCHIVED: 'archived'
} as const;

export const SOURCE_TYPES = {
  CSV_UPLOAD: 'CSV Upload',
  SALES_NAVIGATOR: 'Sales Navigator'
} as const;

export const CHANGE_TYPES = {
  POSITIVE: 'positive',
  NEUTRAL: 'neutral',
  NEGATIVE: 'negative'
} as const;

export interface LeadList {
  id: string;
  name: string;
  source: 'CSV Upload' | 'Sales Navigator';
  totalLeads: number;
  filteredLeads: number | string;
  activeFilters: string;
  status: AUDIENCE_LIST_STATUSES;
  created: string;
  icon: React.ReactNode;
}

export interface ActionButtonsProps {
  onAddList: () => void;
}

// Audiencelists specific interfaces
export interface AudiencelistsActionButtonsProps {
  onAddList: () => void;
  onConnectSalesNavigator: () => void;
}

export interface AudiencelistsAddListDrawerProps {
  visible: boolean;
  importMethod: typeof IMPORT_METHODS[keyof typeof IMPORT_METHODS];
  listName: string;
  uploadedFile: File | null;
  salesNavigatorUrl: string;
  onClose: () => void;
  onOk: () => void;
  onImportMethodChange: (value: typeof IMPORT_METHODS[keyof typeof IMPORT_METHODS]) => void;
  onListNameChange: (value: string) => void;
  onFileUpload: (file: File) => boolean;
  onRemoveFile: () => void;
  onSalesNavigatorUrlChange: (value: string) => void;
  isProcessing?: boolean;
  listNameError?: string;
}

export interface ConnectSalesNavigatorDrawerProps {
  visible: boolean;
  onClose: () => void;
  onConnectLinkedIn: () => void;
}

// Lead Enrichment specific interfaces
export interface LeadEnrichmentActionButtonsProps {
  onAddList: () => void;
}

export interface LeadEnrichmentAddListDrawerProps {
  visible: boolean;
  listName: string;
  uploadedFile: File | null;
  onClose: () => void;
  onOk: () => void;
  onListNameChange: (value: string) => void;
  onFileUpload: (file: File) => boolean;
  onRemoveFile: () => void;
  isProcessing?: boolean;
  listNameError?: string;
}

export interface AudienceList {
  id: string;
  deleted_at: string | null;
  deleted_by_cascade: boolean;
  created_at: string;
  updated_at: string;
  list_name: string;
  source_name: string;
  attachment: string;
  file_type: string | null;
  file_name: string;
  created_by_user_id: number;
  process_started_at: string | null;
  process_completed_at: string | null;
  status: number;
  lead_analysis: any | null;
  lead_analysis_file_key: string | null;
  retry_count: number;
  notes: Record<string, any>;
  row_count: number;
  processed_rows: number;
  created_by: any | null;
}

export interface AudienceListsTableProps {
  audienceLists: AudienceList[];
  activeTab: typeof TAB_KEYS[keyof typeof TAB_KEYS];
  filters: any;
  total: number;
  leadListsLoading: boolean;
  onTabChange: (key: typeof TAB_KEYS[keyof typeof TAB_KEYS]) => void;
  onViewList: (record: AudienceList) => void;
  onMoreActions: (record: AudienceList) => void;
  onDeleteList: (record: AudienceList) => void;
  setFilters: (filter: any) => void;
  exportUploadedFile: (id: string) => void;
}

export interface Lead {
  id: string;
  name: string;
  title: string;
  company: string;
  industry: string;
  companySize: string;
  matchScore: number;
  selected: boolean;
  email?: string;
  emailStatus?: string;
  seniority?: string;
  departments?: string;
  city?: string;
  state?: string;
  country?: string;
}

export enum FilterStatus {
  NOTIFICATION_RECEIVED = 0,
  PROCESSING = 1,
  PROCESSED = 2,
  FAILED = 3,
  INITIALIZED = 4
}

export interface LeadListDetailPageProps {
  listId: string;
  onClose: () => void;
  filters: any[]
  filtersLoading: boolean;
  filtersMetaData: {
    count: number;
    page_size: number;
    page_count: number;
    has_next: boolean;
  };
  addFilterLoading: boolean;
  deleteFilterLoading: boolean;
  getFilters: (params: any) => void;
  addFilter: (filter: any) => void;
  toggleFilter: (filterId: string, is_active: boolean) => void;
  deleteFilter: (filterId: string) => void;
  getFilteredLeads: (params: any) => void;
  clearFilters: () => void;
  filteredLeads: any[];
  filteredLeadsLoading: boolean;
  exportFilteredLeads: (params: any) => void;
  leadInfo: any;
  getFiltersStatus: () => void;
}


export interface SummaryStat {
  title: string;
  value: string;
  change?: string;
  changeType?: typeof CHANGE_TYPES[keyof typeof CHANGE_TYPES];
}

export interface SummaryCardsProps {
  stats: SummaryStat[];
  isLoading?: boolean;
}

// Lead Enrichment specific types
export interface LeadEnrichmentTableProps {
  client: Client;
  audienceLists: AudienceList[];
  activeTab: typeof TAB_KEYS[keyof typeof TAB_KEYS];
  filters: any;
  total: number;
  leadListsLoading: boolean;
  onTabChange: (key: typeof TAB_KEYS[keyof typeof TAB_KEYS]) => void;
  onViewList: (record: AudienceList) => void;
  onMoreActions: (record: AudienceList) => void;
  onDeleteList: (record: AudienceList) => void;
  setFilters: (filter: any) => void;
  exportUploadedFile: (id: string) => void;
}